import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button, { ButtonIcon } from "@waytek/MobiusOverrides/Button/Button";
import { fontFamily, mediaQueriesBP } from "@waytek/Utilities/variables";
import { useEmblaCarousel } from "embla-carousel/react";
import debounce from "lodash/debounce";
import React, { useEffect, useState } from "react";
import { css, ThemeProps, withTheme } from "styled-components";

export const enum fields {
    groupTitle = "groupTitle",
    showCarousel = "showCarousel",
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.groupTitle]: string;
        [fields.showCarousel]: boolean;
    };
}

export type Props = OwnProps & ThemeProps<BaseTheme>;

export interface BlogGroupWidgetStyles {
    textWrapper?: InjectableCss;
    cardGroupWrapper?: InjectableCss;
    cardGroup?: InjectableCss;
    title?: TypographyPresentationProps;
    disclaimerMessage?: TypographyPresentationProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowButton?: ButtonPresentationProps;
}

export const blogGroupWidgetStyles: BlogGroupWidgetStyles = {
    textWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            border-radius: 0 0 6px 6px;
            padding: 22px 0 28px;
            height: 100%;
            width: 100%;
        `,
    },
    cardGroupWrapper: {
        css: css`
            position: relative;
        `,
    },
    cardGroup: {
        css: css`
            & > div {
                display: flex;
                flex-wrap: wrap;

                & > div[data-widget="Custom/BlogCardWidget"],
                & > .blog-card__component {
                    width: calc(25% - 15px);

                    ${mediaQueriesBP.maxLG} {
                        display: flex;
                        position: relative;
                        margin: 10px;
                    }

                    ${mediaQueriesBP.minLG} {
                        margin-right: 20px;

                        &:nth-child(4n) {
                            margin-right: 0;
                        }

                        &:nth-child(n + 5) {
                            margin-top: 20px;
                        }
                    }
                }

                .blog-card__component {
                    height: auto;
                }
            }
        `,
    },
    title: {
        forwardAs: "h3",
        fontFamily,
        size: "48px",
        lineHeight: "57px",
        weight: 500,
        ellipsis: true,
        css: css`
            margin-bottom: 26px;

            ${mediaQueriesBP.maxMD} {
                font-size: 26px;
                line-height: 30px;
                margin-bottom: 34px;
            }
        `,
    },
    disclaimerMessage: {
        transform: "initial",
        size: "24px",
        weight: 700,
        css: css`
            margin-bottom: 13px;
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            position: absolute;
            top: 50%;
            left: 0;
            transform: translate(calc(50% - 20px), -50%);

            &::after {
                content: none;
            }

            &:not(:disabled):hover {
                background: none;
                color: ${getColor("text.main")};

                ${ButtonIcon} {
                    color: ${getColor("text.main")};
                }
            }

            &:focus {
                outline: none;
            }

            ${mediaQueriesBP.minLG} {
                display: none;
            }
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            position: absolute;
            top: 50%;
            right: -20px;
            transform: translate(-50%, -50%);

            &::after {
                content: none;
            }

            &:not(:disabled):hover {
                background: none;
                color: ${getColor("text.main")};

                ${ButtonIcon} {
                    color: ${getColor("text.main")};
                }
            }

            &:focus {
                outline: none;
            }

            ${mediaQueriesBP.minLG} {
                display: none;
            }
        `,
    },
};

export const BlogGroupWidget = ({ id, fields: { groupTitle, showCarousel }, theme: { breakpoints } }: Props) => {
    const [deviceCarousel, setDeviceCarousel] = useState(false);
    const [deviceSize, setDeviceSize] = useState(4);
    const [nodesSizeCondition, setNodesSizeCondition] = useState(false);
    const [emblaRef, embla] = useEmblaCarousel();

    useEffect(() => {
        const handleWindowSize = () => {
            const maxMobileIdx = breakpoints.keys.indexOf("md");
            const maxMobile = breakpoints.values[maxMobileIdx];
            const maxTabletIdx = breakpoints.keys.indexOf("lg");
            const maxTablet = breakpoints.values[maxTabletIdx];

            let size = 4;
            if (window.innerWidth < 425) {
                size = 1;
            } else if (window.innerWidth < maxMobile) {
                size = 2;
            } else if (window.innerWidth < maxTablet) {
                size = 3;
            }
            setDeviceCarousel(size < 4);
            setDeviceSize(size);
        };
        handleWindowSize();
        window.addEventListener("resize", debounce(handleWindowSize, 200));
    }, []);

    useEffect(() => {
        if (!embla || !showCarousel) {
            return;
        }

        embla.on("reInit", () => {
            setNodesSizeCondition(embla.slideNodes().length > deviceSize);
        });
    }, [embla, deviceSize]);

    useEffect(() => {
        if (!embla || !showCarousel) {
            return;
        }

        if (deviceCarousel) {
            embla!.reInit({
                align: "start",
                draggable: true,
                loop: true,
                slidesToScroll: deviceSize,
            });
        } else {
            embla!.reInit({
                align: "start",
                draggable: false,
                loop: false,
            });
        }
        embla!.scrollTo(0);
    }, [embla, deviceCarousel, deviceSize]);

    const notCarouselStyles = css`
        & > div {
            ${mediaQueriesBP.maxLG} {
                margin: -10px;
                width: calc(100% + 20px);
            }

            @media screen and (max-width: 424px) {
                width: 100%;
            }

            & > div[data-widget="Custom/BlogCardWidget"],
            & > .blog-card__component {
                height: auto;

                ${mediaQueriesBP.minMDMaxLg} {
                    width: calc(100% / 3 - 20px);
                    flex: 0 0 calc(100% / 3 - 20px);
                }

                ${mediaQueriesBP.maxMD} {
                    width: calc(50% - 20px);
                    flex: 0 0 calc(50% - 20px);
                }

                ${mediaQueriesBP.maxSM} {
                    width: 100%;
                    flex: 0 0 100%;
                }
            }

            & > div[data-widget="Custom/BlogCardWidget"] > .blog-card__component {
                height: 100%;
            }
        }
    `;

    const carouselStyles = css`
        ${mediaQueriesBP.maxLG} {
            overflow: hidden;

            & > div {
                flex-wrap: initial;
                justify-content: flex-start;

                ${mediaQueriesBP.maxLG} {
                    margin: 0;
                    width: 100%;
                }

                & > div {
                    ${mediaQueriesBP.minMDMaxLg} {
                        width: calc(100% / 3 - 15px);
                        flex: 0 0 calc(100% / 3 - 15px);
                    }

                    ${mediaQueriesBP.maxMD} {
                        width: calc(50% - 10px);
                        flex: 0 0 calc(50% - 10px);
                    }

                    ${mediaQueriesBP.maxSM} {
                        width: 100%;
                        flex: 0 0 100%;
                    }
                }
            }
        }
    `;

    const cardGroupStyles = css`
        ${blogGroupWidgetStyles.cardGroup?.css}
        ${showCarousel ? carouselStyles : notCarouselStyles}
    `;

    const cargGroupWrapperStyles = css`
        ${mediaQueriesBP.maxLG} {
            padding: ${nodesSizeCondition ? "0 28px" : "0"};
        }
    `;

    const { isInShell, isEditing } = useShellContext();

    return (
        <StyledWrapper {...blogGroupWidgetStyles.textWrapper}>
            {groupTitle && <Typography {...blogGroupWidgetStyles.title}>{groupTitle}</Typography>}
            {isInShell && isEditing && (
                <Typography {...blogGroupWidgetStyles.disclaimerMessage}>
                    Please be aware that you should add only 4 blog cards
                </Typography>
            )}
            <StyledWrapper {...blogGroupWidgetStyles.cardGroupWrapper}>
                {showCarousel && nodesSizeCondition && (
                    <Button
                        {...blogGroupWidgetStyles.prevArrowButton}
                        onClick={() => embla && embla.scrollPrev()}
                        data-test-selector="prevBtn"
                    >
                        <ButtonIcon src={ChevronLeft} />
                    </Button>
                )}

                <StyledWrapper css={cargGroupWrapperStyles}>
                    {showCarousel && (
                        <StyledWrapper css={cardGroupStyles} ref={emblaRef}>
                            <Zone contentId={id} zoneName="BlogGroupCardsZone" />
                        </StyledWrapper>
                    )}
                    {!showCarousel && (
                        <StyledWrapper css={cardGroupStyles}>
                            <Zone contentId={id} zoneName="BlogGroupCardsZone" />
                        </StyledWrapper>
                    )}
                </StyledWrapper>

                {showCarousel && nodesSizeCondition && (
                    <Button
                        {...blogGroupWidgetStyles.nextArrowButton}
                        onClick={() => embla && embla.scrollNext()}
                        data-test-selector="nextBtn"
                    >
                        <ButtonIcon src={ChevronRight} />
                    </Button>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withTheme(BlogGroupWidget),
    definition: {
        group: "Blog" as any,
        icon: "LinkList",
        fieldDefinitions: [
            {
                name: fields.groupTitle,
                displayName: "Blog Group Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.showCarousel,
                displayName: "Should use carousel behavior?",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
